import { formatUnits } from 'ethers/lib/utils';
import React, { useEffect, useState } from 'react';

import { useWeb3State } from '../../hooks';
import { useStarStakingContract } from '../../hooks/stars-staking.hook';
import { DesktopRewardsAccordion } from './DesktopRewardsAccordion.component';
import { MobileRewardsAccordion } from './MobileRewardsAccordion.component';
import styles from './RewardsPool.module.css';

export enum PoolStatus {
  Active = 'Active',
  Upcoming = 'Upcoming',
  Closed = 'Closed',
}

export interface Props {
  poolName: string;
  apr: string;
  index: number;
  depositLength: number;
  endBlock: number;
  starsPerBlock: number;
  totalDepositedLP: number;
  blockNumber: number;
}

export interface PoolInfo {
  lockupRemaining: (number | undefined)[];
  stakedAmount: (number | undefined)[];
  rewardAmount: (number | undefined)[];
  depositValuesTotal: number[];
}

export const RewardsPool = (props: Props) => {
  const { walletDetails, isInitialized } = useWeb3State();
  const { pendingSTARS, getLockupRemaining, getDeposit, contractInitialized } =
    useStarStakingContract();

  const [lockup, setLockup] = useState<(number | undefined)[]>([]);
  const [stakedAmount, setStakedAmount] = useState<(number | undefined)[]>([]);
  const [rewardAmount, setRewardAmount] = useState<(number | undefined)[]>([]);
  const [depositValuesTotal, setDespositValuesTotal] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  const getInfo = async () => {
    if (!walletDetails.address || !contractInitialized) {
      return;
    }

    const poolInfo: PoolInfo = {
      lockupRemaining: [],
      stakedAmount: [],
      rewardAmount: [],
      depositValuesTotal: [],
    };

    for (let index = 0; index < props.depositLength; index++) {
      const [lockupRemainingInfo, stakedInfo, rewardInfo] = await Promise.all([
        getLockupRemaining(props.index, walletDetails.address, index),
        getDeposit(props.index, walletDetails.address, index),
        pendingSTARS(props.index, walletDetails.address, index),
      ]);

      poolInfo.lockupRemaining.push(
        lockupRemainingInfo.blocksLeft?.toNumber() ?? 0,
      );
      poolInfo.stakedAmount.push(
        parseFloat(formatUnits(stakedInfo.deposit?.amount ?? 0, 18)),
      );
      poolInfo.rewardAmount.push(
        +parseFloat(formatUnits(rewardInfo.pendingStars ?? 0, 18)).toFixed(6),
      );
      poolInfo.depositValuesTotal.push(
        +parseFloat(formatUnits(stakedInfo.deposit?.amount ?? 0, 18)) +
          +parseFloat(formatUnits(rewardInfo.pendingStars ?? 0, 18)),
      );
    }

    setDespositValuesTotal(poolInfo.depositValuesTotal);
    setLockup(poolInfo.lockupRemaining);
    setStakedAmount(poolInfo.stakedAmount);
    setRewardAmount(poolInfo.rewardAmount);
    setLoading(false);
  };

  useEffect(
    () => void getInfo(),
    [contractInitialized, walletDetails, isInitialized],
  );

  if (loading || props.depositLength <= 0) {
    return null;
  }

  return (
    <>
      {[...Array(props.depositLength)].map((_, index: number) => (
        <div key={index}>
          {depositValuesTotal[index] > 0 && (
            <div
              key={index}
              className={
                styles.poolCardContainer +
                ' w-full rounded-full px-3 py-0 box-border flex flex-col items-center gap-4'
              }
            >
              <div className={styles.desktopView}>
                <DesktopRewardsAccordion
                  poolId={props.index}
                  depositId={index}
                  poolName={props.poolName}
                  apr={props.apr}
                  totalDepositedLP={stakedAmount[index]}
                  rewardAmount={parseFloat(
                    rewardAmount[index]?.toFixed(2) ?? '0',
                  )}
                  timeLock={lockup[index]}
                  isActive={props.blockNumber <= props.endBlock}
                  getRewardInfo={getInfo}
                  endBlock={props.endBlock}
                  starsPerBlock={props.starsPerBlock}
                  totalPoolDepositedLP={props.totalDepositedLP}
                  blockNumber={props.blockNumber}
                />
              </div>
              <div className={styles.mobileView + ' w-full'}>
                <MobileRewardsAccordion
                  poolId={props.index}
                  depositId={index}
                  poolName={props.poolName}
                  apr={props.apr}
                  totalDepositedLP={stakedAmount[index]}
                  rewardAmount={parseFloat(
                    rewardAmount[index]?.toFixed(2) ?? '0',
                  )}
                  timeLock={lockup[index]}
                  isActive={props.blockNumber <= props.endBlock}
                  getRewardInfo={getInfo}
                  endBlock={props.endBlock}
                  starsPerBlock={props.starsPerBlock}
                  totalPoolDepositedLP={props.totalDepositedLP}
                  blockNumber={props.blockNumber}
                />
              </div>
            </div>
          )}
        </div>
      ))}
    </>
  );
};
